import { AppTemplateType, WebAppAuthType } from "../AppTemplateType";
import { ShopifyAppType } from "./AssistantMetadata";

export const MAX_SUMMON_PROMPT_LENGTH = 10000;
export const UnknownUserEmail = "anonymous@gadget.dev";

export const AppClassificationTypes = ["Shopify", "BigCommerce", "Web", "ChatGPT", "Unsupported", "Unknown"] as const;
export type AppClassificationType = (typeof AppClassificationTypes)[number];

export const AuthClassificationTypes = ["Single tenant", "Multi tenant", "No tenant"] as const;
export type AuthClassificationType = (typeof AuthClassificationTypes)[number];

export type AppClassification = {
  appTemplateType: AppTemplateType | null;
  shopifyAppType: ShopifyAppType | null;
  appType: AppClassificationType;
  authType: AuthClassificationType;
  name: string;
  slug: string;
  summary: string;
  hasBackend: boolean;
  hasDatabase: boolean;
  hasFrontend: boolean;
};

export const appClassificationTypeToAppTemplateType = (
  appClassificationType: AppClassificationType,
  authClassificationType?: AuthClassificationType | null
): AppTemplateType | null => {
  if (appClassificationType === "Shopify") {
    return AppTemplateType.ShopifyApp;
  } else if (appClassificationType === "BigCommerce") {
    return AppTemplateType.BigCommerceApp;
  } else if (appClassificationType === "Web" && authClassificationType === "Multi tenant") {
    return AppTemplateType.WebAppWithAuth;
  } else if (appClassificationType === "Web" && authClassificationType === "Single tenant") {
    return AppTemplateType.InternalTool;
  } else if (appClassificationType === "Web" && authClassificationType === "No tenant") {
    return AppTemplateType.Empty;
  }

  return null;
};

export const authClassificationTypeToShopifyAppType = (authClassificationType: AuthClassificationType): ShopifyAppType | null => {
  if (authClassificationType === "Multi tenant") {
    return ShopifyAppType.Public;
  } else {
    return ShopifyAppType.Custom;
  }
};

export const authClassificationTypeToWebAppAuthType = (authClassificationType: AuthClassificationType): WebAppAuthType | null => {
  if (authClassificationType === "Multi tenant") {
    return WebAppAuthType.EmailPassword;
  } else if (authClassificationType === "Single tenant") {
    return WebAppAuthType.InviteAuth;
  } else if (authClassificationType === "No tenant") {
    return WebAppAuthType.NoAuth;
  }

  return null;
};

export const appTemplateTypeToAppClassificationType = (appTemplateType: AppTemplateType): AppClassificationType | null => {
  if (appTemplateType === AppTemplateType.ShopifyApp) {
    return "Shopify";
  } else if (appTemplateType === AppTemplateType.WebAppWithAuth) {
    return "Web";
  } else if (appTemplateType === AppTemplateType.InternalTool) {
    return "Web";
  } else if (appTemplateType === AppTemplateType.Empty) {
    return "Web";
  } else if (appTemplateType === AppTemplateType.BigCommerceApp) {
    return "BigCommerce";
  }

  return null;
};

export const shopifyAppTypeToAuthClassificationType = (shopifyAppType: ShopifyAppType): AuthClassificationType | null => {
  if (shopifyAppType === ShopifyAppType.Public) {
    return "Multi tenant";
  } else if (shopifyAppType === ShopifyAppType.Custom) {
    return "Single tenant";
  }

  return null;
};

export const webAppAuthTypeToAuthClassificationType = (webAppAuthType: WebAppAuthType): AuthClassificationType | null => {
  if (webAppAuthType === WebAppAuthType.EmailPassword) {
    return "Multi tenant";
  } else if (webAppAuthType === WebAppAuthType.InviteAuth) {
    return "Single tenant";
  } else if (webAppAuthType === WebAppAuthType.NoAuth) {
    return "No tenant";
  }

  return null;
};

export const appTemplateTypeToAppAndAuthClassificationTypes = (
  appTemplateType: AppTemplateType,
  opts?: { shopifyAppType?: ShopifyAppType }
): {
  appType: AppClassificationType;
  authType: AuthClassificationType | undefined;
} => {
  if (appTemplateType === AppTemplateType.ShopifyApp) {
    return {
      appType: "Shopify",
      authType: (opts?.shopifyAppType && shopifyAppTypeToAuthClassificationType(opts.shopifyAppType)) ?? undefined,
    };
  } else if (appTemplateType === AppTemplateType.WebAppWithAuth) {
    return {
      appType: "Web",
      authType: "Multi tenant",
    };
  } else if (appTemplateType === AppTemplateType.InternalTool) {
    return {
      appType: "Web",
      authType: "Single tenant",
    };
  } else if (appTemplateType === AppTemplateType.Empty) {
    return {
      appType: "Web",
      authType: "No tenant",
    };
  } else {
    throw new Error(`Unknown app template type: ${appTemplateType}`);
  }
};
export const appDisplayTypeForSummon = (appTemplateType: AppTemplateType, shopifyAppType?: ShopifyAppType | null): string => {
  if (appTemplateType === AppTemplateType.ShopifyApp) {
    return shopifyAppType ? `${shopifyAppType} Shopify App` : "Shopify App";
  } else if (appTemplateType === AppTemplateType.WebAppWithAuth) {
    return "Web app with multi-party auth";
  } else if (appTemplateType === AppTemplateType.InternalTool) {
    return "Web app with single-party auth";
  } else if (appTemplateType === AppTemplateType.Empty) {
    return "Web app with no auth";
  } else if (appTemplateType === AppTemplateType.BigCommerceApp) {
    return "BigCommerce App";
  } else {
    return "Unknown";
  }
};

export interface SummonExample {
  appType: AppClassificationType;
  authType: AuthClassificationType;
  label: string;
  prompt: string;
}

export interface GadgetAppSummon {
  summonKey: string;
  prompt: string;
  settingUpMessage: string;
  settingUpSteps: string[];
  summary: string;
  appName: string;
  userEmailHash: string;
  appTemplateType: AppTemplateType;
  shopifyAppType?: ShopifyAppType;
  appDisplayType: string;
  loadingMessage: string;
}

export type SummonWebAppRequirements = {
  coreModels: { identifier: string; description: string }[];
};

export type SummonShopifyRequirements = {
  shopifyModels: { identifier: string; description: string }[];
  additionalModels: { identifier: string; description: string }[];
};

export type SummonAppConfig = {
  appTemplateType: AppTemplateType;
  shopifyAppType?: ShopifyAppType | null;
};
